"use client"

import { useState, useEffect, useCallback, useMemo, useRef, JSX } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { listWorkflows, getHpcJobsMetadata, HpcJobsMetadataResponse } from "@/services/hpcJobsService"
import { HpcJobFilters, HpcWorkflow } from "@/types/hpc-jobs"
import {
  transformWorkflowData,
  isWorkflowActive,
  isoDateDaysAgo,
  parseJobDateRange,
  JOB_DATE_RANGE_OPTIONS,
  DEFAULT_JOB_DATE_RANGE,
  JobDateRange,
} from "@/lib/hpc-jobs-utils"
import { HpcWorkflowsTable } from "@/components/hpc-jobs/HpcWorkflowsTable"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { RefreshCw, Search, BookOpen } from "lucide-react"
import LoadingSpinner from "@/components/shared/LoadingSpinner"

/** Page title for the standard (non-admin) view. */
const PAGE_TITLE = "HPC Jobs"

/** Page title for the admin view. */
const ADMIN_PAGE_TITLE = "HPC Jobs (Admin)"

/**
 * Auto-refresh interval in seconds. The backend recalculates workflow status
 * and counts about once a minute, so polling more often mostly refetches the
 * same data.
 */
const AUTO_REFRESH_INTERVAL = 30

/**
 * URL to the Confluence documentation explaining awsmap workflows on AWS Batch.
 * Opened in a new tab from the page header so users can learn how HPC jobs work.
 */
const HPC_DOCS_URL =
  "https://malariaatlas.atlassian.net/wiki/spaces/MAP/pages/315129867/awsmap+workflows+on+AWS+batch"

/**
 * HPC Jobs interface component.
 *
 * Displays a table of workflows, one row per workflow, with two tabs
 * (Running / Completed), client-side text filtering, and optional
 * auto-refresh. Workflow status and task counts come from the backend's
 * workflow records via the /workflows endpoint; individual jobs are reached
 * through the workflow detail page.
 *
 * When `isAdmin` is false (default), the component shows only the current
 * user's jobs and renders a standard user-facing header with a Documentation
 * link and an optional "Admin View" button for managers.
 *
 * When `isAdmin` is true, the component verifies that the authenticated user
 * has the `is_manager` permission (redirecting to /computing/hpc if not), then
 * fetches all users' jobs and renders an admin-specific header with a "My Jobs"
 * back-link and additional user-filter input.
 *
 * @param isAdmin - Whether to render the admin variant of the interface.
 *   Defaults to false.
 */
export default function HpcJobsInterface({ isAdmin = false }: { isAdmin?: boolean }): JSX.Element {
  const { user, getAccessToken } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get the username from the Auth0 user object (only used in user mode)
  const username = user?.username ?? user?.name ?? null

  // ─── URL param: active tab ───────────────────────────────────────────────────

  // Tab state derived from URL query param
  const activeTab = (searchParams.get("tab") === "completed" ? "completed" : "running") as
    | "running"
    | "completed"

  /**
   * Updates the active tab URL parameter.
   *
   * @param tab - The new tab to set ("running" | "completed").
   */
  const setActiveTab = (tab: "running" | "completed"): void => {
    const params = new URLSearchParams(searchParams)
    if (tab === "completed") {
      params.set("tab", "completed")
    } else {
      params.delete("tab")
    }
    setSearchParams(params, { replace: true })
  }

  // ─── URL param: date range ───────────────────────────────────────────────────

  // Look-back window (in days) derived from URL query param, validated against
  // the supported options and defaulting to 7 days.
  const dateRange: JobDateRange = parseJobDateRange(searchParams.get("range"))

  /**
   * Updates the look-back window URL parameter.
   *
   * The default window is represented by the absence of the param (mirroring
   * how the tab param is handled) to keep URLs clean.
   *
   * @param range - The new look-back window expressed as a JobDateRange value.
   */
  const setDateRange = (range: JobDateRange): void => {
    const params = new URLSearchParams(searchParams)
    if (range === DEFAULT_JOB_DATE_RANGE) {
      params.delete("range")
    } else {
      params.set("range", String(range))
    }
    setSearchParams(params, { replace: true })
  }

  // ─── Metadata state ──────────────────────────────────────────────────────────

  // Full metadata response — only needed in admin mode for the is_manager guard.
  const [metadata, setMetadata] = useState<HpcJobsMetadataResponse | null>(null)

  // Whether the initial metadata fetch has completed (used to gate the
  // permission loading spinner in admin mode).
  const [metadataChecked, setMetadataChecked] = useState<boolean>(false)

  // In user mode we only need the boolean flag for showing the "Admin View" button.
  const [isManager, setIsManager] = useState<boolean>(false)

  // ─── Data state ──────────────────────────────────────────────────────────────

  const [allWorkflows, setAllWorkflows] = useState<HpcWorkflow[]>([])
  // True when the backend had more workflows than its limit and returned only
  // the newest ones.
  const [isTruncated, setIsTruncated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Filter state — admin mode adds the `user` filter on top of `jobName`.
  const [filters, setFilters] = useState<HpcJobFilters>({ jobName: "", user: "" })

  // ─── Auto-refresh state ──────────────────────────────────────────────────────

  const [autoRefresh, setAutoRefresh] = useState<boolean>(false)
  const [countdown, setCountdown] = useState<number>(AUTO_REFRESH_INTERVAL)
  const pendingRefresh = useRef<boolean>(false)

  // ─── Effects ─────────────────────────────────────────────────────────────────

  /**
   * Fetches user metadata on component mount.
   *
   * In admin mode: verifies the `is_manager` flag and redirects non-managers
   * to /computing/hpc for safety, then sets `metadataChecked` to lift the
   * permission loading guard.
   *
   * In user mode: only reads `is_manager` to conditionally display the
   * "Admin View" button. Failure is non-fatal so the rest of the page still
   * renders normally.
   */
  useEffect(() => {
    async function fetchMetadata(): Promise<void> {
      try {
        const token = await getAccessToken()
        const info = await getHpcJobsMetadata(token)

        if (isAdmin) {
          setMetadata(info)
          // Redirect non-managers to the regular page
          if (!info.is_manager) {
            navigate("/computing/hpc", { replace: true })
          }
        } else {
          // Non-fatal in user mode — just controls the Admin View button
          setIsManager(info.is_manager)
        }
      } catch (err) {
        console.error("Failed to fetch HPC jobs metadata:", err)
        if (isAdmin) {
          // On error, redirect to regular page for safety
          navigate("/computing/hpc", { replace: true })
        }
      } finally {
        if (isAdmin) {
          setMetadataChecked(true)
        }
      }
    }
    fetchMetadata()
  }, [getAccessToken, navigate, isAdmin])

  /**
   * Fetches workflows from the API.
   *
   * In admin mode, passes `user: "all"` to retrieve every user's workflows
   * (the backend validates manager status server-side). In user mode,
   * passes the authenticated user's username to scope the response to
   * their own workflows only.
   *
   * Passes `finished: true` when on the "completed" tab so the backend
   * includes finished workflows (it then returns active ones too, which the
   * tab filter drops). The `start` parameter restricts results to the
   * currently selected look-back window. The backend's default limit (200,
   * newest first) applies; `truncated` says whether it cut anything off.
   *
   * @returns A promise that resolves once the state has been updated.
   */
  const fetchWorkflows = useCallback(async (): Promise<void> => {
    // In user mode, wait until we have the username before fetching
    if (!isAdmin && !username) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const token = await getAccessToken()
      const response = await listWorkflows(
        {
          // Admin fetches all users; regular users scope to themselves
          user: isAdmin ? "all" : username!,
          finished: activeTab === "completed",
          // Only fetch workflows submitted within the selected look-back
          // window; the backend defaults the end of the window to "now".
          start: isoDateDaysAgo(dateRange),
        },
        token
      )

      setAllWorkflows(response.workflows.map(transformWorkflowData))
      setIsTruncated(response.truncated)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load workflows"
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [getAccessToken, activeTab, username, dateRange, isAdmin])

  /**
   * Triggers an initial data fetch (and re-fetches when tab/date/user change).
   *
   * In admin mode: waits for the metadata check to complete and confirms
   * manager status before fetching, to avoid an unnecessary API call for
   * non-managers who will be redirected anyway.
   *
   * In user mode: waits until the username is available from the Auth context.
   */
  useEffect(() => {
    if (isAdmin) {
      if (metadataChecked && metadata?.is_manager) {
        fetchWorkflows()
      }
    } else {
      if (username) {
        fetchWorkflows()
      }
    }
  }, [fetchWorkflows, isAdmin, metadataChecked, metadata?.is_manager, username])

  /**
   * Auto-refresh side-effect.
   *
   * Starts a 1-second countdown timer when `autoRefresh` is enabled. When
   * the countdown reaches zero, calls `fetchWorkflows` if the tab is visible,
   * otherwise sets a `pendingRefresh` flag and resets when the page becomes
   * visible again via the `visibilitychange` event.
   *
   * Cleans up both the interval and the event listener on teardown.
   */
  useEffect(() => {
    if (!autoRefresh) {
      setCountdown(AUTO_REFRESH_INTERVAL)
      return
    }

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible" && pendingRefresh.current) {
        pendingRefresh.current = false
        fetchWorkflows()
        setCountdown(AUTO_REFRESH_INTERVAL)
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (document.visibilityState === "visible") {
            fetchWorkflows()
            return AUTO_REFRESH_INTERVAL
          } else {
            pendingRefresh.current = true
            return 0
          }
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [autoRefresh, fetchWorkflows])

  // ─── Derived data (client-side filtering) ────────────────────────────────────

  /**
   * Returns the filtered list of workflows for the active tab.
   *
   * Applies the active-tab status filter (active workflows on "Running",
   * everything else on "Completed"), then the text-based `jobName` filter
   * (matched against the workflow name) and, in admin mode, the `user` filter.
   * The backend already returns workflows newest first, so no sorting is
   * needed.
   *
   * @returns Array of HpcWorkflow objects matching the current filters and tab.
   */
  const filteredWorkflows = useMemo((): HpcWorkflow[] => {
    return allWorkflows.filter((workflow) => {
      // Tab filter based on the workflow's own status
      if (activeTab === "running" && !isWorkflowActive(workflow.status)) return false
      if (activeTab === "completed" && isWorkflowActive(workflow.status)) return false

      // Workflow name text filter
      const nameMatch =
        !filters.jobName ||
        workflow.workflowName?.toLowerCase().includes(filters.jobName.toLowerCase())

      // User filter (admin mode only)
      const userMatch =
        !isAdmin ||
        !filters.user ||
        workflow.user.toLowerCase().includes(filters.user.toLowerCase())

      return nameMatch && userMatch
    })
  }, [allWorkflows, activeTab, filters, isAdmin])

  // ─── Admin-only early returns ─────────────────────────────────────────────────

  // Show a loading spinner while verifying manager permissions (admin mode only)
  if (isAdmin && !metadataChecked) {
    return <LoadingSpinner loadingContent="Checking permissions..." />
  }

  // Show access denied if not a manager (should have redirected, but guards
  // against any race condition where the redirect hasn't fired yet)
  if (isAdmin && !metadata?.is_manager) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2">
        <p className="text-lg font-semibold">Access Denied</p>
        <p className="text-sm text-muted-foreground">
          This page is only available to administrators.
        </p>
      </div>
    )
  }

  // ─── Derived display values ───────────────────────────────────────────────────

  const pageTitle = isAdmin ? ADMIN_PAGE_TITLE : PAGE_TITLE

  const cardDescription = isAdmin ? (    "Admin view — all users"
  ) : username ? (
    <>
      Showing workflows for: <span className="font-medium text-foreground">{username}</span>
    </>
  ) : (
    "Your AWS Batch workflows"
  )

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
      <title>{pageTitle} - MAP Apps Platform</title>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>
              <h1>{pageTitle}</h1>
            </CardTitle>
            <CardDescription>{cardDescription}</CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {/* User mode: docs link + optional Admin View button */}
            {!isAdmin && (
              <a
                href={HPC_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <BookOpen className="h-4 w-4" />
                Documentation
              </a>
            )}
            {!isAdmin && isManager && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/computing/hpc/admin")}
              >
                Admin View
              </Button>
            )}

            {/* Admin mode: back to personal view */}
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/computing/hpc")}
              >
                My Jobs
              </Button>
            )}

            {/* Auto-refresh toggle (shared) */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="h-4 w-4"
              />
              Auto-refresh
              {autoRefresh && (
                <span className="text-muted-foreground">({countdown}s)</span>
              )}
            </label>

            {/* Manual refresh button (shared) */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchWorkflows()}
              disabled={isLoading}
              aria-label="Refresh workflows"
            >
              {isLoading ? (
                <LoadingSpinner variant="default" loadingContent="Refreshing..." />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col min-h-0 gap-4">
        {/* Status and date-range toggles */}
        <div className="flex justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              <div className="inline-flex rounded-md border">
                <Button
                  variant={activeTab === "running" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTab("running")}
                  className={`rounded-r-none border-r ${activeTab === "running" ? "text-foreground" : ""}`}
                >
                  Running
                </Button>
                <Button
                  variant={activeTab === "completed" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTab("completed")}
                  className={`rounded-l-none ${activeTab === "completed" ? "text-foreground" : ""}`}
                >
                  Completed
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Last:</span>
              <div className="inline-flex rounded-md border">
                {JOB_DATE_RANGE_OPTIONS.map((option, index) => (
                  <Button
                    key={option}
                    variant={dateRange === option ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setDateRange(option)}
                    className={`${index === 0 ? "rounded-r-none border-r" : "rounded-l-none"} ${dateRange === option ? "text-foreground" : ""}`}
                  >
                    {option} days
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Filter inputs — admin mode adds a user filter */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter by workflow name..."
                aria-label="Filter by workflow name"
                value={filters.jobName}
                onChange={(e) => setFilters((f) => ({ ...f, jobName: e.target.value }))}
                className="pl-8 w-auto"
              />
            </div>
            {isAdmin && (
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter by user..."
                  aria-label="Filter by user"
                  value={filters.user}
                  onChange={(e) => setFilters((f) => ({ ...f, user: e.target.value }))}
                  className="pl-8 w-auto"
                />
              </div>
            )}
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="rounded-md border border-destructive bg-destructive/10 p-4">
            <p className="text-sm text-destructive">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchWorkflows()}
              className="mt-2"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Initial loading state */}
        {isLoading && allWorkflows.length === 0 && (
          <LoadingSpinner loadingContent="Loading HPC workflows..." />
        )}

        {/* The backend returned only the newest workflows in the window */}
        {isTruncated && !error && (
          <p role="status" className="text-sm text-muted-foreground">
            Only the newest {allWorkflows.length} workflows from the last {dateRange} days are
            shown; older ones in this period are left out.
          </p>
        )}

        {/* Workflows table */}
        {(!isLoading || allWorkflows.length > 0) && !error && (
          <HpcWorkflowsTable
            workflows={filteredWorkflows}
            currentTab={activeTab}
            isAdmin={isAdmin}
            onJobCancelled={fetchWorkflows}
          />
        )}
      </CardContent>
    </Card>
  )
}
